import api from '@/api_alv'
import i18n from '@/assets/i18n/i18n'
import { translateInsightDomain } from '@/pages/8_servicecenter/1_insights/_helpers/insightsDomain'

// Recursively collect all text nodes from a Storyblok richtext object.
function extractPlainText(node) {
    if (!node) return ''
    if (typeof node === 'string') return node
    let out = ''
    if (node.text) out += node.text + ' '
    const children = node.content || node.children
    if (Array.isArray(children)) {
        for (const child of children) out += extractPlainText(child)
    }
    return out
}

const state = {
    // List (displayed = filtered)
    insights: [],
    // Full unfiltered set, source for the client-side search
    allInsights: [],
    expandedInsights: [],

    insightsSearch: "",
}

const getters = {
    getInsights:            (state) => state.insights,
    getExpandedInsights:    (state) => state.expandedInsights,
    getInsightsDetails:     (state) => state.expandedInsights,
    getInsightsSearch:      (state) => state.insightsSearch,
}

const mutations = {
    setInsights:            (state, value) => { state.insights = value },
    setAllInsights:         (state, value) => { state.allInsights = value },
    setExpandedInsights:    (state, value) => { state.expandedInsights = Array.isArray(value) ? value : [] },
    setInsightsDetails:     (state, value) => { state.expandedInsights = Array.isArray(value) ? value : [] },
    setInsightsSearch:      (state, value) => { state.insightsSearch = value },
}

const actions = {
    async fetchInsights({ commit, dispatch }, language = null) {
        commit('setIsLoading', true, { root: true })
        
        try {
            // Use passed language or fallback to i18n locale (same as Dashboard)
            const currentLanguage = language || i18n.locale || 'de'
            
            // Use existing fetchDashboardInsights with dateLimitWeeks: null (no limit)
            const response = await dispatch('fetchDashboardInsights', {
                language: currentLanguage,
                dateLimitWeeks: null
            }, { root: true })
            
            if (response && response.stories) {
                // Remove duplicates based on story id (can occur when story matches multiple audiences)
                const uniqueStories = response.stories.filter((story, index, self) =>
                    index === self.findIndex(s => s.id === story.id)
                )
                
                // Transform stories to list format with id for BaseDataTableList
                const transformedInsights = uniqueStories.map((story, index) => {
                    const headline = story.content && story.content.Headline ? story.content.Headline : ''
                    const domain = story.content && story.content.Domain ? story.content.Domain : ''
                    const bodyText = story.content && story.content.Paragraph ? extractPlainText(story.content.Paragraph) : ''
                    const domainText = translateInsightDomain(domain, i18n.t.bind(i18n))

                    return {
                        id: story.id || index,
                        alvId: story.uuid || `INSIGHT-${index}`,
                        headline: headline,
                        publishDate: story.content && story.content.PublishDate ? story.content.PublishDate : story.published_at,
                        audience: story.content && story.content.Audience ? story.content.Audience : '',
                        domain: domain,
                        priority: story.content && story.content.Priority ? story.content.Priority : 'normal',
                        // Precomputed lowercased haystacks for client-side search
                        searchText: `${headline} ${bodyText} ${domainText}`.toLowerCase(),
                        domainText: domainText.toLowerCase(),
                        // Store full content for readonly
                        content: story.content || {},
                        fullStory: story
                    }
                })
                
                // Sort by date descending (newest first)
                transformedInsights.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
                
                commit('setAllInsights', transformedInsights)
                dispatch('applyInsightsSearch')
            } else {
                commit('setAllInsights', [])
                dispatch('applyInsightsSearch')
            }
        } catch (error) {
            console.error('Error fetching insights:', error)
            commit('setAllInsights', [])
            dispatch('applyInsightsSearch')
        } finally {
            commit('setIsLoading', false, { root: true })
        }
    },

    // Client-side filtering of allInsights. Note: the BaseListPage search triggers
    // fetchInsights, which reloads all stories from Storyblok before calling this.
    // - Multiple words are AND-combined and each must match at the start of a word.
    // - If the query contains ">", it is treated as an ALV domain search and matched
    //   against the area text only (not title/full text).
    applyInsightsSearch({ state, commit }) {
        const raw = (state.insightsSearch || '').trim().toLowerCase()
        const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        // Word start = preceded by start of string or a non-(letter/digit) char (incl. German umlauts).
        const wordStartMatcher = (token) => new RegExp('(?:^|[^a-z0-9äöüß])' + escapeRegExp(token))

        let filtered
        if (raw === '') {
            filtered = state.allInsights
        } else if (raw.includes('>')) {
            // Domain-only search: split on whitespace and ">", match against the area text only
            const matchers = raw.split(/[\s>]+/).filter(Boolean).map(wordStartMatcher)
            filtered = matchers.length === 0
                ? state.allInsights
                : state.allInsights.filter(item => matchers.every(matcher => matcher.test(item.domainText)))
        } else {
            const matchers = raw.split(/\s+/).filter(Boolean).map(wordStartMatcher)
            filtered = state.allInsights.filter(item => matchers.every(matcher => matcher.test(item.searchText)))
        }

        commit('setInsights', filtered)
        commit('setTotalListed', filtered.length, { root: true })
        commit('setTotalRecords', filtered.length, { root: true })
        commit('setCurrentPage', 1, { root: true })
    },
    
    async fetchInsightsDetails({ commit, state }, alvId) {
        // Find the insight in current list and add to expanded
        const insight = state.insights.find(item => item.alvId === alvId)
        
        if (insight) {
            // Check if already expanded
            const existingIndex = state.expandedInsights.findIndex(item => item.id === insight.id)
            
            if (existingIndex === -1) {
                // Add to expanded items
                commit('setExpandedInsights', [...state.expandedInsights, insight])
            }
        }
        
        commit('setIsLoading', false, { root: true })
    }
}

export default { state, getters, actions, mutations }
